/**
 * Logging interceptor for debugging and monitoring RPC calls
 */

import { Interceptor, RpcEnvelope, RpcResponse } from "../types/protocol";

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

export interface Logger {
  debug: (message: string, ...args: any[]) => void;
  info: (message: string, ...args: any[]) => void;
  warn: (message: string, ...args: any[]) => void;
  error: (message: string, ...args: any[]) => void;
}

export class LoggingInterceptor implements Interceptor {
  public readonly name = "LoggingInterceptor";
  public readonly order = 1000; // Run last to see final state

  private logLevel: LogLevel = LogLevel.INFO;
  private enabled: boolean = true;
  private sensitiveFields: string[] = [];
  private logger: Logger = console;

  /**
   * Set the minimum log level
   */
  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
  }

  /**
   * Enable or disable logging
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Set fields that should be redacted in logs
   */
  setSensitiveFields(fields: string[]): void {
    this.sensitiveFields = fields;
  }

  /**
   * Set a custom logger implementation
   */
  setCustomLogger(logger: Logger): void {
    this.logger = logger;
  }

  async intercept(
    direction: "inbound" | "outbound",
    message: RpcEnvelope | RpcResponse,
    next: () => Promise<any>,
  ): Promise<any> {
    if (!this.enabled) {
      return next();
    }

    // Log the message before processing
    this.logMessage(direction, message);

    return next();
  }

  private logMessage(
    direction: "inbound" | "outbound",
    message: RpcEnvelope | RpcResponse,
  ): void {
    if (this.isRpcEnvelope(message)) {
      this.logRequest(direction, message);
    } else {
      this.logResponse(direction, message);
    }
  }

  private logRequest(
    direction: "inbound" | "outbound",
    envelope: RpcEnvelope,
  ): void {
    const logData = this.sanitizeMessage(envelope);

    if (envelope.kind === "notify") {
      this.log(
        LogLevel.INFO,
        `[Bifrost] ${direction === "outbound" ? "Outbound" : "Inbound"} RPC notification:`,
        logData,
      );
    } else {
      this.log(
        LogLevel.INFO,
        `[Bifrost] ${direction === "outbound" ? "Outbound" : "Inbound"} RPC call:`,
        logData,
      );
    }
  }

  private logResponse(
    direction: "inbound" | "outbound",
    response: RpcResponse,
  ): void {
    const logData = this.sanitizeMessage(response);

    if (response.error) {
      this.log(
        LogLevel.ERROR,
        `[Bifrost] ${direction === "outbound" ? "Outbound" : "Inbound"} RPC error:`,
        logData,
      );
    } else {
      this.log(
        LogLevel.INFO,
        `[Bifrost] ${direction === "outbound" ? "Outbound" : "Inbound"} RPC response:`,
        logData,
      );
    }
  }

  private sanitizeMessage(message: RpcEnvelope | RpcResponse): any {
    if (this.sensitiveFields.length === 0) {
      return message;
    }

    const sanitized = { ...message };

    for (const field of this.sensitiveFields) {
      if (field in sanitized) {
        (sanitized as any)[field] = "[REDACTED]";
      }
    }

    return sanitized;
  }

  private log(level: LogLevel, message: string, ...args: any[]): void {
    if (level < this.logLevel) {
      return;
    }

    switch (level) {
      case LogLevel.DEBUG:
        this.logger.debug(message, ...args);
        break;
      case LogLevel.INFO:
        this.logger.info(message, ...args);
        break;
      case LogLevel.WARN:
        this.logger.warn(message, ...args);
        break;
      case LogLevel.ERROR:
        this.logger.error(message, ...args);
        break;
    }
  }

  private isRpcEnvelope(
    message: RpcEnvelope | RpcResponse,
  ): message is RpcEnvelope {
    return "kind" in message;
  }
}
